import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MSALSingleton from '../utils/msalInstance';
import { environmentVars } from '../config/authConfig';

interface Message {
  id: string;
  timestamp: string;
  query: string;
  response: string;
  role: string;
  user: string;
}

const MessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialized) return;

    const fetchMessages = async () => {
      try {
        const msalInstance = MSALSingleton.getInstance();
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          console.log('No accounts found, redirecting to login');
          navigate('/');
          return;
        }

        const account = accounts[0];

        // Get the authentication token
        let authToken;
        try {
          const tokenResult = await msalInstance.acquireTokenSilent({
            account: account,
            scopes: ['user.read']
          });
          authToken = tokenResult?.accessToken;
        } catch (tokenError) {
          console.error('Token acquisition failed:', tokenError);
          navigate('/');
          return;
        }

        if (!authToken) {
          console.log('No token available, redirecting to login');
          navigate('/');
          return;
        }

        const provider = localStorage.getItem('authProvider') || 'microsoft';

        // Construct the query URL with user parameter
        const queryParams = new URLSearchParams({
          user: account.username
        });
        const url = `${environmentVars.apiUrlMessages}?${queryParams.toString()}`;

        // Set up request with both tokens
        const request = new Request(url, {
          method: 'GET',
          headers: new Headers({
            'Authorization': `Bearer ${ environmentVars.apiKey}`,
            'X-Auth-Token': authToken || '',
            'X-Auth-Provider': provider,
            'Content-Type': 'application/json'
          })
        });

        const response = await fetch(request);

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `Failed to fetch messages: ${response.statusText}`);
        }

        const data = await response.json();
        const sortedMessages = [...data.messages].sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        setMessages(sortedMessages);
      } catch (err) {
        console.error('Error fetching messages:', err);
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';

        if (errorMessage.includes('interaction_required') ||
            errorMessage.includes('consent_required') ||
            errorMessage.includes('login_required')) {
          navigate('/login');
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [initialized, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="mb-2">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-red-700 underline hover:text-red-800"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-start justify-center p-6 font-sans">
      <div className="w-full max-w-4xl relative z-10 mt-8">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
          <div className="bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 py-4 px-6 flex items-center justify-between">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <div className="w-3 h-3 bg-green-500 rounded-full" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Message History</h2>
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No messages found</p>
              ) : (
                messages.map((message) => (
                  <div key={message.id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm text-gray-500">
                        {formatDate(message.timestamp)}
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-2">Question:</p>
                        <p className="text-gray-600">{message.query}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                        <p className="text-gray-600">{message.response}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;